import type { ReactNode } from "react";
import { Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

// Mecanismo único de seleção usado pelos dois lados do vínculo
// usuário↔veículo (Usuários → Veículos em routes/users.tsx e
// Veículos → Vincular usuário em routes/index.tsx) — mesma busca, mesma
// lista com checkbox, mesmo estado vazio. As duas telas batem no mesmo
// backend/relação (ver src/services/vehicle-permissions.service.ts); este
// componente garante que também não haja duas implementações de UI
// divergentes para a mesma operação. `renderRow` fica a cargo de cada
// tela porque o conteúdo visual da linha é naturalmente diferente
// (veículo: ícone colorido/placa/motorista; usuário: nome/e-mail).
export interface PermissionLinkPickerProps<T> {
  items: T[];
  getId: (item: T) => number;
  isSelected: (id: number) => boolean;
  onToggle: (id: number) => void;
  renderRow: (item: T, checked: boolean) => ReactNode;
  query: string;
  onQueryChange: (value: string) => void;
  searchPlaceholder?: string;
  emptyText?: string;
  loading?: boolean;
  loadingText?: string;
  maxHeightClassName?: string;
}

export function PermissionLinkPicker<T>({
  items,
  getId,
  isSelected,
  onToggle,
  renderRow,
  query,
  onQueryChange,
  searchPlaceholder = "Buscar...",
  emptyText = "Nada encontrado.",
  loading = false,
  loadingText = "Carregando…",
  maxHeightClassName = "max-h-[420px]",
}: PermissionLinkPickerProps<T>) {
  if (loading) {
    return <div className="p-8 text-center text-xs text-muted-foreground">{loadingText}</div>;
  }

  return (
    <>
      <div className="relative mb-3">
        <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          placeholder={searchPlaceholder}
          className="h-9 pl-8"
        />
      </div>

      <div
        className={cn(
          "space-y-1.5 overflow-y-auto rounded-lg border border-border bg-background p-2",
          maxHeightClassName,
        )}
      >
        {items.length === 0 ? (
          <div className="p-8 text-center text-xs text-muted-foreground">{emptyText}</div>
        ) : (
          items.map((item) => {
            const id = getId(item);
            const checked = isSelected(id);
            return (
              <label
                key={id}
                className={cn(
                  "flex cursor-pointer items-center gap-3 rounded-md border border-transparent p-2 transition-colors hover:bg-muted/60",
                  checked && "border-primary/40 bg-primary/5",
                )}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => onToggle(id)}
                  className="h-4 w-4 accent-primary"
                />
                {renderRow(item, checked)}
              </label>
            );
          })
        )}
      </div>
    </>
  );
}
